import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause } from 'lucide-react';
import { motion } from 'framer-motion';

const VisualBreathingBox = () => {
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState('inhale');
  const [count, setCount] = useState(4);
  const countRef = useRef(count);

  useEffect(() => {
    countRef.current = count;
  }, [count]);

  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      if (countRef.current > 1) {
        setCount(prev => prev - 1);
        return;
      }

      setPhase(current => {
        if (current === 'inhale') return 'hold1';
        if (current === 'hold1') return 'exhale';
        if (current === 'exhale') return 'hold2';
        return 'inhale';
      });
      setCount(4);
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive]);

  const getPhaseText = () => {
    if (phase === 'inhale') return 'Breathe In';
    if (phase === 'exhale') return 'Breathe Out';
    return 'Hold';
  };

  const getBoxScale = () => {
    if (phase === 'inhale') return 1.5;
    if (phase === 'hold1') return 1.5;
    if (phase === 'exhale') return 0.7;
    return 0.7; // hold2: stay at the post-exhale size
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E0F7FA] to-[#B2EBF2] relative overflow-hidden">
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-12">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-4xl font-bold text-[#2D3748]">Visual Breathing Box</h1>
          
          <button
            data-testid="toggle-breathing"
            onClick={() => setIsActive(!isActive)}
            className="bg-[#81B29A] text-white px-6 py-3 rounded-full font-semibold hover:-translate-y-1 transition-all shadow-lg flex items-center gap-2"
          >
            {isActive ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            {isActive ? 'Stop' : 'Start'}
          </button>
        </header>

        <div className="flex items-center justify-center min-h-[500px]">
          <div className="text-center">
            <motion.div
              animate={{ scale: isActive ? getBoxScale() : 1 }}
              transition={{ duration: 4, ease: 'easeInOut' }}
              className="w-64 h-64 bg-gradient-to-br from-[#81B29A] to-[#6A9B84] rounded-3xl shadow-2xl flex items-center justify-center mx-auto mb-8"
            >
              <div className="text-white text-center">
                <p className="text-3xl font-bold mb-4">{getPhaseText()}</p>
                <p className="text-7xl font-bold">{count}</p>
              </div>
            </motion.div>
            
            <p className="text-2xl text-[#4A5568] font-medium">4-4-4-4 Pattern</p>
          </div>
        </div>

        <div className="mt-12 bg-white rounded-2xl p-6 text-center shadow-lg">
          <p className="text-[#4A5568] text-lg">
            Follow the box breathing technique: Inhale for 4, hold for 4, exhale for 4, hold for 4. Repeat.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VisualBreathingBox;